
import { computed, ComputedRef, defineComponent, ref, watch } from 'vue'
// Components
import TInput from '@/components/Ticketing/Controls/TInput.vue'
import PrimaryButton from '@/components/Buttons/PrimaryButton.vue'
// Utils
import { isValidDate } from '@/utils/dateUtils'
import { createVuexHelpers } from 'vue2-helpers'
import { getValidationCode } from '@/utils/helpers'
// Types
import {
	CardTypes,
	VALIDATION_EXP_CARD_DATE_VALIDATION_CODES
} from '@/types/constantsTypes'
import { ChargeInfo } from '@/types/entitiesTypes'
import { GetEventTicket, ValidateExpDate } from '@/types/storeTypes'
import { CatchError } from '@/types/commonTypes'
import { AxiosError } from 'axios'

export default defineComponent({
	name: 'CardConfirmation',

	emits: ['card-confirmed', 'set-loading'],

	components: { PrimaryButton, TInput },

	props: {
		disabled: {
			type: Boolean,
			default: false
		},

		loading: {
			type: Boolean,
			default: false
		}
	},

	setup(props, { emit }) {
		// Store
		const { useGetters, useActions } = createVuexHelpers()

		const {
			chargeInfo,
			eventTicketId
		}: {
			chargeInfo: ComputedRef<ChargeInfo>
			eventTicketId: ComputedRef<string>
		} = useGetters('events', ['chargeInfo', 'eventTicketId'])

		const {
			venueId
		}: {
			venueId: ComputedRef<string>
		} = useGetters('venue', ['venueId'])

		const {
			validateExpDate
		}: { validateExpDate: ValidateExpDate } = useActions('payment', [
			'validateExpDate'
		])

		const {
			getEventTicket
		}: { getEventTicket: GetEventTicket } = useActions('events', [
			'getEventTicket'
		])

		// refs
		const cardExpirationDate = ref('')
		const expirationDateError = ref('')

		// Computed
		const lastCardDigits = computed(
			() => chargeInfo.value.publicCardData.cardLastDigits || ''
		)
		const tenderCode = computed(
			() => chargeInfo.value.publicCardData.tenderCode
		)

		const cardType = computed(() => {
			if (!tenderCode.value && tenderCode.value !== 0) return 'unknown'

			return CardTypes[tenderCode.value] || 'unknown'
		})

		// Watchers
		watch(
			() => props.disabled,
			value => {
				if (value) {
					expirationDateError.value = ''
					cardExpirationDate.value = ''
				}
			}
		)

		// Methods
		const validateMonth = (): void => {
			expirationDateError.value = ''

			const isExpirationDateValid = isValidDate(cardExpirationDate.value)

			if (!isExpirationDateValid || cardExpirationDate.value.length < 5) {
				expirationDateError.value =
					'Month is not valid. Please use MM/YY format'
			}
		}

		const onSubmit = async (): Promise<void> => {
			validateMonth()

			if (expirationDateError.value) return

			emit('set-loading', true)

			const [month, year] = cardExpirationDate.value.split('/')

			const payload = {
				expirationYear: year,
				expirationMonth: month,
				venueId: venueId.value,
				eventTicketId: eventTicketId.value
			}

			try {
				await validateExpDate(payload)
				emit('card-confirmed')
			} catch (e) {
				const error = e as CatchError
				const validationCode = getValidationCode(error)

				if (
					error instanceof AxiosError &&
					VALIDATION_EXP_CARD_DATE_VALIDATION_CODES.includes(validationCode)
				) {
					await getEventTicket({
						venueId: venueId.value,
						eventTicketId: eventTicketId.value
					})

					expirationDateError.value = 'Expiration date is wrong. Try again'
				}
				emit('set-loading', false)
			}
		}

		const onExpirationDateChanged = (): void => {
			validateMonth()
		}

		const onExpirationDateValidInput = (value: string): void => {
			if (value.length === 1 && +value > 1) {
				cardExpirationDate.value = `0${value}`

				return
			}

			cardExpirationDate.value = value
		}

		return {
			onSubmit,
			onExpirationDateChanged,
			onExpirationDateValidInput,
			cardExpirationDate,
			expirationDateError,
			lastCardDigits,
			cardType
		}
	}
})
